import React, { FunctionComponent, MouseEvent, useEffect, useRef, useState } from 'react';
import { format } from 'react-string-format';
import { has } from 'lodash';
import {
    Box,
    Fab,
    IconButton,
    Chip,
    makeStyles,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
    Button,
    Menu,
    MenuItem,
} from '@material-ui/core';
import moment from 'moment';
import { v1 as uuid } from 'uuid';
import { Skeleton } from '@material-ui/lab';
import { Delete } from '@material-ui/icons';
import ArrowDown from '@material-ui/icons/ArrowDropDown';
import BackToTopArrow from '@material-ui/icons/ArrowUpward';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import { GetApp as DownloadIcon } from '@material-ui/icons';
import TableSortLabel from '@material-ui/core/TableSortLabel';
import Checkbox from '@material-ui/core/Checkbox';
import { displayCurrencyAmount, getFormattedField } from '../../utils/utils';
import InfiniteScroller from '../../components/InfiniteScroller';
import prettyBytes from 'pretty-bytes';
import { AutoSizer } from 'react-virtualized';
import { Order } from '../../store/modules/application/types';

type DataTableProps = {
    className?: string;
    columns: any;
    data: Array<unknown>;
    component?: any;
    count: number;
    hasMore: boolean;
    loadMore: Function;
    onClickRow: Function;
    classes?: any;
    sortBy?: any;
    sortOrder?: Order;
    deleteFlag?: boolean;
    downloadFlag?: boolean;
    actionFlag?: boolean;
    isTableRowSelectable?: boolean;
    approveActionHandler?: Function;
    denyActionHandler?: Function;
    handleDeleteCase?: Function;
    handleDownloadCase?: Function;
    setSortColumn?: Function;
    setSortOrder?: Function;
    scrollToTopTrigger?: any;
    handleRowClick?: Function;
    additionalData?: any;
    errorMessage?: string;
    handleAction?: Function;
    buttonLabel?: string;
    handleCheckIconClick?: Function;
};
const useStyles = makeStyles({
    root: {
        '&>.infinite-scroll-component__outerdiv': {
            height: '100%',
        },
    },
    stickyHeader: {
        backgroundColor: '#FFFFFF',
    },
    container: {
        height: '100%',
        backgroundColor: '#FFFFFF',
        width: '100%',
    },
    editIcon: {
        opacity: 0,
    },

    rowContainer: {
        cursor: 'pointer',
        '&:hover ': {
            backgroundColor: '#eeeeee',
            transition: 'ease-in 200ms',
            '& button': {
                opacity: 1,
                transition: 'ease-in 200ms',
            },
            '& .MuiChip-root': {
                opacity: 1,
                transition: 'ease-in 200ms',
            },
        },
    },
    visuallyHidden: {
        border: 0,
        clip: 'rect(0 0 0 0)',
        height: 1,
        margin: -1,
        overflow: 'hidden',
        padding: 0,
        position: 'absolute',
        top: 20,
        width: 1,
    },
    sortableIcon: {
        color: 'rgb(0 0 0) !important',
        marginTop: 2,
        fontSize: 20,
        '&.active': {
            color: 'black',
        },
    },
    fab: {
        position: 'absolute',
        bottom: 40,
        right: 40,
    },
    actionChips: {
        '& .MuiChip-root': {
            margin: '0 4px',
            opacity: 0,
        },
        display: 'flex',
    },
    denyActionChip: {
        color: '#0277BD',
        borderColor: '#0277BD',
    },
    approveActionChip: {
        color: '#FFFFFF',
        backgroundColor: '#0277BD',
    },
    selectedResultCount: {
        height: '20px',
        width: '92px',
        fontWeight: 500,
        color: '#333333',
        fontFamily: 'Roboto',
        fontSize: '14px',
        letterSpacing: ' 0.15px',
        lineHeight: '20px',
    },
    title: {
        fontSize: 13,
        fontWeight: 600,
        color: '#333333',
        padding: 10,
    },
    MenuList: {
        marginTop: 50,
        height: '400px',
    },
    downloadIcon: {
        color: '#333333;',
        fontSize: '16px',
        lineHeight: '24px',
        textAlign: 'right',
        fontWeight: 600,
        padding: '0px',
    },
    downloadMenuItem: {
        border: '1px solid rgba(0,0,0,0.12)',
        borderRadius: 4,
    },
    errorLabel: {
        display: 'flex',
        alignItems: 'center',
        color: '#B71C1C',
        fontSize: '14px',
        margin: 10,
        marginRight: 8,
        fontWeight: 500,
    },
    tickSize: {
        transform: 'scale(1.3)',
    },

    //TODO: This is a fix for the same cell style
    // cellStyle: {
    //     fontSize: '14px',
    // },
});

const DataTable: FunctionComponent<DataTableProps> = (props) => {
    const ref = useRef<any>(null);

    const {
        data,
        columns,
        sortBy,
        hasMore,
        loadMore,
        sortOrder,
        deleteFlag,
        onClickRow,
        actionFlag,
        buttonLabel,
        errorMessage,
        downloadFlag,
        setSortOrder,
        handleAction,
        setSortColumn,
        additionalData,
        handleDeleteCase,
        denyActionHandler,
        handleDownloadCase,
        scrollToTopTrigger,
        handleCheckIconClick,
        approveActionHandler,
        isTableRowSelectable = false,
    } = props;

    const [selectedRows, setSelectedRows] = useState<string[]>([]);
    const [showBackToTop, setShowBackToTop] = useState<boolean>(false);
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [isNoOptionSelected, setIsNoOptionSelected] = useState<boolean>(false);
    const anchorRef = useRef<HTMLButtonElement>(null);

    const classes = useStyles();

    //Call scroll to top whenever the trigger changes/updates
    useEffect(() => {
        scrollToTopTrigger && scrollToTop();
    }, [scrollToTopTrigger]);

    const createSortHandler = (property: any, columnType: string) => (event: any) => {
        handleRequestSort(event, property, columnType);
    };

    const getSortByField = () => {
        return sortBy && (Array.isArray(sortBy) ? sortBy[0].split(' ')[0] : sortBy.split(' ')[0]);
    };

    //TODO: This code would serve as a base code for future tickets for generic multi-sort functionality.
    // Since in the scope of this ticket it is now just used for 'Violations' only, so I have made some
    // changes as it would only work for Violations tab. As the scope would go wider I'll remove the specifics.
    const isViolationData = data.some((row) => has(row, 'violationType'));

    const handleRequestSort = (event: MouseEvent<unknown>, property: any, columnType: string) => {
        let isAsc;
        let sortingOrder = '';

        if (setSortColumn && setSortOrder) {
            scrollToTop();

            if (isViolationData) {
                //TODO: This code would serve as a base code for future tickets for generic multi-sort functionality.
                // Since in the scope of this ticket it is now just used for 'Violations' only, so I have made some
                // changes as it would only work for Violations tab. As the scope would go wider I'll remove the specifics.
                if (columnType === 'template') {
                    //TODO: For future support, 'templateSortColumns' is declared as an string array so it
                    // supports multiple sort functionality when implemented by services side
                    const templateSortColumns: string[] = [];
                    isAsc = property && getSortByField() === property && sortOrder === 'asc';
                    sortingOrder = isAsc ? 'desc' : 'asc';
                    templateSortColumns.push(`${property} ${sortingOrder}`);
                    setSortColumn(templateSortColumns[0]);
                    setSortOrder(sortingOrder);
                } else {
                    isAsc = getSortByField() === property && sortOrder === 'asc';
                    sortingOrder = isAsc ? 'desc' : 'asc';
                    setSortColumn(`${property} ${sortingOrder}`);
                    setSortOrder(sortingOrder);
                }
            } else {
                const isAsc = sortBy === property && sortOrder === 'asc';
                setSortColumn(property);
                setSortOrder(isAsc ? 'desc' : 'asc');
            }
        }
    };

    const scrollToTop = () => {
        ref.current.scrollTo({ top: 0, behavior: 'smooth' }); //scroll to the top of the table
    };

    const handleScroll = (event: any) => {
        if (event?.target?.scrollTop > 200 && !showBackToTop) {
            setShowBackToTop(true);
        } else if (event?.target?.scrollTop === 0) {
            setShowBackToTop(false);
        }
    };

    const setActiveSortColumnDirection = (column: any) => {
        const columnType = column.id.split('|')[1];
        let isActiveSort: boolean;
        //TODO: This code would serve as a base code for future tickets for generic multi-sort functionality.
        // Since in the scope of this ticket it is now just used for 'Violations' only, so I have made some
        // changes as it would only work for Violations tab. As the scope would go wider I'll remove the specifics.
        if (columnType === 'template' && isViolationData) {
            isActiveSort = column?.sortFields ? (getSortByField() === column.sortFields[0] ? true : false) : false;
        } else {
            isActiveSort = column?.sortFields ? (getSortByField() === column.field ? true : false) : false;
        }
        return isActiveSort;
    };

    const setSortDirection = (column: any, sortOrder: string | undefined) => {
        const columnType = column.id.split('|')[1];
        let direction;
        //TODO: This code would serve as a base code for future tickets for generic multi-sort functionality.
        // Since in the scope of this ticket it is now just used for 'Violations' only, so I have made some
        // changes as it would only work for Violations tab. As the scope would go wider I'll remove the specifics.
        if (columnType === 'template' && isViolationData) {
            const field = column.sortFields[0];
            direction = getSortByField() === field ? sortOrder : 'asc';
        } else {
            direction = getSortByField() === column.field ? sortOrder : 'asc';
        }
        return direction === 'asc' ? 'asc' : 'desc';
    };

    //TODO: Might need this functionality in future.
    // const handleSelectAllClick = (event: ChangeEvent<HTMLInputElement>) => {
    //     if (event.target.checked) {
    //         setSelectedRows([...selectedRows, data.map((n: any) => n.id) as any]);
    //     } else setSelectedRows([]);
    // };

    const handleCheckboxClick = (event: MouseEvent<HTMLElement>, id: string) => {
        event.stopPropagation();
        setIsNoOptionSelected(false);
        const selectedIndex = selectedRows.indexOf(id);
        let newSelected: string[] = [];

        if (selectedIndex === -1) {
            newSelected = newSelected.concat(selectedRows, id);
        } else if (selectedIndex === 0) {
            newSelected = newSelected.concat(selectedRows.slice(1));
        } else if (selectedIndex === selectedRows.length - 1) {
            newSelected = newSelected.concat(selectedRows.slice(0, -1));
        } else if (selectedIndex > 0) {
            newSelected = newSelected.concat(
                selectedRows.slice(0, selectedIndex),
                selectedRows.slice(selectedIndex + 1),
            );
        }

        setSelectedRows(newSelected);
        handleCheckIconClick && handleCheckIconClick(newSelected);
    };

    const isSelected = (id: string) => {
        return selectedRows.indexOf(id) !== -1;
    };

    const handleToggle = () => {
        if (selectedRows.length) {
            setIsNoOptionSelected(false);
            setIsMenuOpen(true);
        } else {
            setIsNoOptionSelected(true);
            setIsMenuOpen(false);
        }
    };

    const handleClose = () => {
        setIsMenuOpen(false);
    };

    return (
        <AutoSizer disableWidth className="fullWidth">
            {({ height }) => (
                <Box style={{ height }} className={classes.root}>
                    <InfiniteScroller
                        data={data}
                        dataHasMore={hasMore}
                        loadMore={loadMore}
                        loader={
                            <div>
                                <Skeleton />
                                <Skeleton />
                            </div>
                        }
                        scrollableTarget="scrollableDiv"
                        handleScroll={handleScroll}
                    >
                        <TableContainer className={classes.container} id="scrollableDiv" ref={ref}>
                            <Table stickyHeader>
                                <TableHead>
                                    {isTableRowSelectable && (
                                        <TableRow>
                                            <TableCell className={classes.stickyHeader} colSpan={12}>
                                                <Box display="flex" justifyContent="space-between">
                                                    <Box display="flex" alignItems="center">
                                                        {selectedRows.length ? (
                                                            <Typography className={classes.selectedResultCount}>
                                                                {selectedRows.length} Selected
                                                            </Typography>
                                                        ) : (
                                                            <></>
                                                        )}
                                                        {isNoOptionSelected && (
                                                            <>
                                                                <ErrorOutlineIcon style={{ color: '#B71C1C' }} />
                                                                <Typography className={classes.errorLabel}>
                                                                    {errorMessage}
                                                                </Typography>
                                                            </>
                                                        )}
                                                    </Box>
                                                    <Box className={classes.downloadMenuItem}>
                                                        <Button
                                                            className={classes.downloadIcon}
                                                            ref={anchorRef}
                                                            aria-controls={
                                                                isMenuOpen ? 'download-notices-list' : undefined
                                                            }
                                                            aria-haspopup="true"
                                                            onClick={handleToggle}
                                                        >
                                                            <Box display="flex" style={{ paddingLeft: 10 }}>
                                                                <DownloadIcon />
                                                            </Box>
                                                            <Typography className={classes.title}>
                                                                {buttonLabel}
                                                            </Typography>
                                                        </Button>
                                                        <Menu
                                                            id="download-menu"
                                                            anchorEl={anchorRef.current}
                                                            keepMounted
                                                            open={isMenuOpen}
                                                            onClose={handleClose}
                                                            className={classes.MenuList}
                                                        >
                                                            {additionalData?.map((item: any) => (
                                                                <MenuItem
                                                                    key={item.id}
                                                                    onClick={() =>
                                                                        handleAction &&
                                                                        handleAction({
                                                                            label: item.name,
                                                                            value: item.id,
                                                                        })
                                                                    }
                                                                >
                                                                    {item.name}
                                                                </MenuItem>
                                                            ))}
                                                        </Menu>
                                                    </Box>
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                    <TableRow>
                                        {isTableRowSelectable && (
                                            <TableCell
                                                className={classes.stickyHeader}
                                                style={{
                                                    fontWeight: 600,
                                                    fontSize: '14px',
                                                    color: '#484848',
                                                }}
                                                colSpan={1}
                                            >
                                                {/* //TODO: Leaving it commented here. Might need in future
                                                <Checkbox
                                                indeterminate={true}
                                                checked={true}
                                                onClick={handleSelectAllClick}
                                            /> */}
                                            </TableCell>
                                        )}
                                        {columns?.map((column: any) => {
                                            const columnDirection = setSortDirection(column, sortOrder);
                                            const activeSortDirection = setActiveSortColumnDirection(column);

                                            return (
                                                <>
                                                    <TableCell
                                                        className={classes.stickyHeader}
                                                        key={column.id}
                                                        align={column.align}
                                                        style={{
                                                            minWidth: column.minWidth,
                                                            fontWeight: 600,
                                                            fontSize: '14px',
                                                            color: '#484848',
                                                        }}
                                                        sortDirection={columnDirection}
                                                    >
                                                        {!column.noSort ? (
                                                            <TableSortLabel
                                                                active={activeSortDirection}
                                                                direction={columnDirection}
                                                                onClick={
                                                                    column?.sortFields
                                                                        ? createSortHandler(
                                                                              column.sortFields[0],
                                                                              column.id.split('|')[1],
                                                                          )
                                                                        : undefined
                                                                }
                                                                IconComponent={ArrowDown}
                                                                hideSortIcon={!column.sortFields}
                                                                classes={{
                                                                    // Override with the active class of sortable icon
                                                                    icon: classes.sortableIcon,
                                                                }}
                                                            >
                                                                {column.label}
                                                                {sortBy === column.field ? (
                                                                    <span className={classes.visuallyHidden}>
                                                                        {sortBy === 'desc'
                                                                            ? 'sorted descending'
                                                                            : 'sorted ascending'}
                                                                    </span>
                                                                ) : null}
                                                            </TableSortLabel>
                                                        ) : (
                                                            column.label
                                                        )}
                                                    </TableCell>
                                                </>
                                            );
                                        })}
                                        <TableCell className={classes.stickyHeader}></TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {data.map((row: any) => {
                                        const isItemSelected = isSelected(row.id);
                                        const labelId = `multi-select-table-checkbox-${row.id}`;

                                        return (
                                            <>
                                                <TableRow
                                                    className={classes.rowContainer}
                                                    hover
                                                    tabIndex={-1}
                                                    key={uuid()}
                                                    onClick={(e: MouseEvent<HTMLElement>) => {
                                                        e.stopPropagation();
                                                        return onClickRow(row.id, e);
                                                    }}
                                                >
                                                    {isTableRowSelectable && (
                                                        <TableCell colSpan={1}>
                                                            <Box>
                                                                <Checkbox
                                                                    className={classes.tickSize}
                                                                    color="primary"
                                                                    checked={isItemSelected}
                                                                    onClick={(e) => handleCheckboxClick(e, row.id)}
                                                                    inputProps={{ 'aria-labelledby': labelId }}
                                                                />
                                                            </Box>
                                                        </TableCell>
                                                    )}
                                                    {columns?.map((column: any) => {
                                                        const configuredFormat = column?.format;
                                                        const valueObj: any = { value: row };
                                                        const valueWithType = column.id.split('|');
                                                        const columnId = valueWithType[0];
                                                        const columnType = valueWithType[1];
                                                        const objectTree = columnId.split('.');
                                                        if (objectTree?.length > 0) {
                                                            objectTree.forEach((element: string) => {
                                                                valueObj.value = valueObj?.value[element];
                                                            });
                                                        } else {
                                                            valueObj.value = row[columnId];
                                                        }
                                                        if (!!valueObj.value) {
                                                            switch (columnType) {
                                                                case 'date':
                                                                    valueObj.value = moment(
                                                                        valueObj.value as string,
                                                                    ).format(configuredFormat ?? 'L');
                                                                    break;
                                                                case 'time':
                                                                    valueObj.value = moment(
                                                                        valueObj.value as string,
                                                                    ).format(configuredFormat ?? 'LT');
                                                                    break;
                                                                case 'currency':
                                                                    valueObj.value = displayCurrencyAmount(
                                                                        valueObj?.value,
                                                                    );
                                                                    break;
                                                                case 'size':
                                                                    valueObj.value =
                                                                        valueObj?.value !== undefined
                                                                            ? prettyBytes(Number(valueObj.value))
                                                                            : '';
                                                                    break;
                                                                case 'timeRange':
                                                                    if (valueObj?.value.end) {
                                                                        valueObj.value =
                                                                            moment(
                                                                                valueObj?.value.start as string,
                                                                            ).format(configuredFormat ?? 'LT') +
                                                                            ' - ' +
                                                                            moment(
                                                                                valueObj?.value.end as string,
                                                                            ).format(configuredFormat ?? 'LT');
                                                                    } else {
                                                                        valueObj.value = moment(
                                                                            valueObj?.value.start as string,
                                                                        ).format(configuredFormat ?? 'LT');
                                                                    }

                                                                    break;
                                                                case 'eventDate':
                                                                    valueObj.value = moment(
                                                                        valueObj?.value as string,
                                                                    ).format(configuredFormat ?? 'dddd L');
                                                                    break;
                                                                case 'dateTimeRange':
                                                                    if (valueObj?.value.end) {
                                                                        valueObj.value =
                                                                            moment(
                                                                                valueObj?.value.start as string,
                                                                            ).format(configuredFormat ?? 'L') +
                                                                            ' ' +
                                                                            moment(
                                                                                valueObj?.value.start as string,
                                                                            ).format(configuredFormat ?? 'LT') +
                                                                            ' - ' +
                                                                            moment(
                                                                                valueObj?.value.end as string,
                                                                            ).format(configuredFormat ?? 'LT');
                                                                    } else if (valueObj?.value.start.length > 10) {
                                                                        valueObj.value =
                                                                            moment(
                                                                                valueObj?.value.start as string,
                                                                            ).format(configuredFormat ?? 'L') +
                                                                            ' ' +
                                                                            moment(
                                                                                valueObj?.value.start as string,
                                                                            ).format(configuredFormat ?? 'LT');
                                                                    } else {
                                                                        valueObj.value = moment(
                                                                            valueObj?.value.start as string,
                                                                        ).format(configuredFormat ?? 'L');
                                                                    }

                                                                    break;
                                                                case 'dateTimeRangeDay':
                                                                    if (valueObj?.value.end) {
                                                                        valueObj.value =
                                                                            moment(
                                                                                valueObj?.value.start as string,
                                                                            ).format(configuredFormat ?? 'dddd L') +
                                                                            ' ' +
                                                                            moment(
                                                                                valueObj?.value.start as string,
                                                                            ).format(configuredFormat ?? 'LT') +
                                                                            ' - ' +
                                                                            moment(
                                                                                valueObj?.value.end as string,
                                                                            ).format(configuredFormat ?? 'LT');
                                                                    } else if (valueObj?.value.start.length > 10) {
                                                                        valueObj.value =
                                                                            moment(
                                                                                valueObj?.value.start as string,
                                                                            ).format(configuredFormat ?? 'dddd L') +
                                                                            ' ' +
                                                                            moment(
                                                                                valueObj?.value.start as string,
                                                                            ).format(configuredFormat ?? 'LT');
                                                                    } else {
                                                                        valueObj.value = moment(
                                                                            valueObj?.value.start as string,
                                                                        ).format(configuredFormat ?? 'dddd L');
                                                                    }
                                                                    break;
                                                                case 'template':
                                                                    const formattedField = getFormattedField(
                                                                        column.field,
                                                                    );
                                                                    valueObj.value = format(
                                                                        formattedField,
                                                                        ...valueObj.value,
                                                                    );
                                                                    break;
                                                                default:
                                                                    break;
                                                            }
                                                        }
                                                        return (
                                                            <TableCell
                                                                //TODO:This is a fix for a future ticket
                                                                //className={classes.cellStyle}
                                                                key={uuid()}
                                                                align={column.align}
                                                                style={{ maxWidth: column.maxWidth }}
                                                            >
                                                                {valueObj.value}
                                                            </TableCell>
                                                        );
                                                    })}
                                                    <TableCell>
                                                        {actionFlag && row.status === 'Submitted' && (
                                                            <Box className={classes.actionChips}>
                                                                <Chip
                                                                    className={classes.denyActionChip}
                                                                    size="small"
                                                                    label="Deny"
                                                                    variant="outlined"
                                                                    onClick={(e: MouseEvent<HTMLElement>) => {
                                                                        denyActionHandler &&
                                                                            denyActionHandler(row.id, e);
                                                                    }}
                                                                />
                                                                <Chip
                                                                    className={classes.approveActionChip}
                                                                    size="small"
                                                                    label="Approve"
                                                                    onClick={(e: MouseEvent<HTMLElement>) => {
                                                                        approveActionHandler &&
                                                                            approveActionHandler(row.id, e);
                                                                    }}
                                                                />
                                                            </Box>
                                                        )}
                                                        {downloadFlag && (
                                                            <IconButton
                                                                onClick={(e: MouseEvent<HTMLElement>) => {
                                                                    handleDownloadCase && handleDownloadCase(row, e);
                                                                }}
                                                                className={classes.editIcon}
                                                            >
                                                                <DownloadIcon />
                                                            </IconButton>
                                                        )}
                                                        {deleteFlag && (
                                                            <IconButton
                                                                onClick={(e: MouseEvent<HTMLElement>) => {
                                                                    handleDeleteCase && handleDeleteCase(row.id, e);
                                                                }}
                                                                className={classes.editIcon}
                                                            >
                                                                <Delete />
                                                            </IconButton>
                                                        )}
                                                    </TableCell>
                                                </TableRow>
                                            </>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        {showBackToTop && (
                            <Tooltip title="Back to Top" placement="top">
                                <Fab
                                    size="small"
                                    className={classes.fab}
                                    aria-label="Back to Top"
                                    onClick={scrollToTop}
                                >
                                    <BackToTopArrow color="primary" />
                                </Fab>
                            </Tooltip>
                        )}
                    </InfiniteScroller>
                </Box>
            )}
        </AutoSizer>
    );
};
export default DataTable;
